import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, PlusIcon, ZapIcon, FilterIcon, ArrowRightIcon, ChevronDownIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { Button } from '../components/ui/Button';
import { CreateAutomationModal } from '../components/automation/CreateAutomationModal';
import { automations, automationInsights, type Automation } from '../data/automation';
import { cn } from '../components/ui/cn';
function StatusToggle({
  status
}: {
  status: Automation['status'];
}) {
  const [on, setOn] = useState(status === 'active');
  return <button onClick={() => setOn(v => !v)} className={cn('relative h-5 w-9 rounded-full transition-colors', on ? 'bg-accent' : 'bg-white/12')} aria-label="Toggle automation" data-id="element-1411" data-mp="1qcavbr4e3p6w">
      <span className={cn('absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform', on ? 'translate-x-4' : 'translate-x-0.5')} data-id="element-1412" data-mp="ezrv48calfyn" />
    </button>;
}
function AutomationRow({
  auto,
  index
}: {
  auto: Automation;
  index: number;
}) {
  const [expanded, setExpanded] = useState(index === 0);
  const Icon = auto.icon;
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.05
  }} className="overflow-hidden rounded-2xl border border-white/[0.06] bg-ink-850" data-id="element-1413" data-mp="3okjcxvzr0yvt">
      <div className="flex items-center gap-4 p-4" data-id="element-1414" data-mp="3tlqkbtxzbw1y">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent-soft" data-id="element-1415" data-mp="3tl7ehbllsy4a">
          <Icon className="h-5 w-5" data-id="element-1416" data-mp="c7bp5v1arppe" />
        </div>
        <div className="min-w-0 flex-1" data-id="element-1417" data-mp="75w2jyjh1b4w">
          <div className="flex items-center gap-2" data-id="element-1418" data-mp="76xpp1drho2z">
            <h3 className="text-[15px] font-medium text-white" data-id="element-1419" data-mp="l55jx1jeybag">{auto.name}</h3>
            <span className={cn('text-[11px]', auto.status === 'active' ? 'text-grow' : 'text-zinc-500')} data-id="element-1420" data-mp="g741mittag01">
              {auto.status === 'active' ? 'Active' : 'Paused'}
            </span>
          </div>
          <p className="mt-0.5 truncate text-[12.5px] text-zinc-500" data-id="element-1421" data-mp="3evizvsyjbwx1">{auto.trigger}</p>
        </div>

        <div className="hidden items-center gap-6 sm:flex" data-id="element-1422" data-mp="3ixnr6l1ighgi">
          <div className="text-right" data-id="element-1423" data-mp="3nr0dgoipwc63">
            <div className="text-[13px] font-semibold text-white" data-id="element-1424" data-mp="3ns1st56rnktm">{auto.runs}</div>
            <div className="text-[10.5px] text-zinc-600" data-id="element-1425" data-mp="3srec5fyfi9rg">runs</div>
          </div>
          <div className="text-right" data-id="element-1426" data-mp="1hflgvr3h6qo">
            <div className="text-[13px] font-semibold text-grow" data-id="element-1427" data-mp="6i3mma91f3ym">{auto.timeSaved}</div>
            <div className="text-[10.5px] text-zinc-600" data-id="element-1428" data-mp="6g92gy5lbfvl">saved</div>
          </div>
          <div className="text-right" data-id="element-1429" data-mp="aivvv3hwt8uw">
            <div className="text-[13px] font-semibold text-white" data-id="element-1430" data-mp="289q6zsyfc0m4">{auto.successRate}%</div>
            <div className="text-[10.5px] text-zinc-600" data-id="element-1431" data-mp="2cb1w7xddyjts">success</div>
          </div>
        </div>

        <StatusToggle status={auto.status} data-id="element-1432" data-mp="24rj14sqvb04u" />
        <button onClick={() => setExpanded(e => !e)} className="rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-white" data-id="element-1433" data-mp="24satzvt2yjrf">
          <ChevronDownIcon className={cn('h-4.5 w-4.5 transition-transform', expanded && 'rotate-180')} data-id="element-1434" data-mp="1v2rcath3svy5" />
        </button>
      </div>

      {expanded && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} className="border-t border-white/[0.06] p-4" data-id="element-1435" data-mp="2nqertu3iowdx">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start" data-id="element-1436" data-mp="2ip7i3phtdn4m">
            {/* Trigger */}
            <div className="flex-1 rounded-xl border border-accent/20 bg-accent/[0.05] p-3" data-id="element-1437" data-mp="2ert9o01yjjdo">
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-accent-soft" data-id="element-1438" data-mp="29sq50gpw3f68">
                <ZapIcon className="h-3.5 w-3.5" data-id="element-1439" data-mp="ybqf1rr0lcfd" /> Trigger
              </div>
              <p className="text-[12.5px] text-zinc-200" data-id="element-1440" data-mp="ydte11a5afw8">{auto.trigger}</p>
            </div>
            <ArrowRightIcon className="mt-6 hidden h-4 w-4 shrink-0 text-zinc-600 lg:block" data-id="element-1441" data-mp="2m6241dezshpq" />
            {/* Conditions */}
            <div className="flex-1 rounded-xl border border-white/[0.06] bg-ink-800 p-3" data-id="element-1442" data-mp="2d7qfdgxortzx">
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-zinc-400" data-id="element-1443" data-mp="2h91wsz6ep8x1">
                <FilterIcon className="h-3.5 w-3.5" data-id="element-1444" data-mp="34yeeknfsupay" /> Conditions
              </div>
              <ul className="space-y-1" data-id="element-1445" data-mp="39y94annu98uf">
                {auto.conditions.map(c => <li key={c} className="text-[12.5px] text-zinc-300" data-id="element-1446" data-mp="2vy83f87kq1ey">
                    · {c}
                  </li>)}
              </ul>
            </div>
            <ArrowRightIcon className="mt-6 hidden h-4 w-4 shrink-0 text-zinc-600 lg:block" data-id="element-1447" data-mp="30z5ihbxaefc8" />
            {/* Actions */}
            <div className="flex-[1.4] rounded-xl border border-white/[0.06] bg-ink-800 p-3" data-id="element-1448" data-mp="1phvz0pe5rnax">
              <div className="mb-2 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-zinc-400" data-id="element-1449" data-mp="1h0vdbq3uejqb">
                Actions
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-1450" data-mp="1cz0goywuwuiv">
                {auto.actions.map((a, i) => <span key={a} className="inline-flex items-center gap-1 rounded-lg bg-white/[0.05] px-2 py-1 text-[11.5px] text-zinc-200 ring-1 ring-inset ring-white/[0.06]" data-id="element-1451" data-mp="1qy9slme152h1" data-mpi={i}>
                    <span className="text-[10px] text-accent-soft" data-id="element-1452" data-mp="tbkmp1g7i5pw">{i + 1}</span>
                    {a}
                  </span>)}
              </div>
            </div>
          </div>
        </motion.div>}
    </motion.div>;
}
export function AutomationStudio() {
  const [automationList, setAutomationList] = useState<Automation[]>(automations);
  const [createOpen, setCreateOpen] = useState(false);
  const totalSaved = '83 hrs';
  const active = automationList.filter(a => a.status === 'active').length;
  const createAutomation = (automation: Automation) => setAutomationList(current => [automation, ...current]);
  return <div data-id="element-1453" data-mp="2fkb1ze53g3qy">
      <PageHeader eyebrow="Automation Studio" title="Operations workflows" subtitle="Keep your website, tour dates, releases, and reporting moving without manual follow-up. Fan CRM and fan outreach live in Fans & VIP." actions={<Button variant="primary" size="sm" onClick={() => setCreateOpen(true)} data-id="element-1455" data-mp="1rpi0sn3v3vd0">
            <PlusIcon className="h-4 w-4" data-id="element-1456" data-mp="1wmhz2bugzttk" /> New workflow
          </Button>} data-id="element-1454" data-mp="2fk1b7oc85sk7" />

      {/* Summary */}
      <div className="mb-6 grid grid-cols-3 gap-3" data-id="element-1457" data-mp="20o3hecprx0b5">
        {[{
        label: 'Active workflows',
        value: `${active}`
      }, {
        label: 'Runs this month',
        value: '758'
      }, {
        label: 'Time saved this month',
        value: totalSaved,
        accent: true
      }].map(s => <div key={s.label} className={cn('rounded-2xl border p-4', s.accent ? 'border-accent/25 bg-accent/[0.06]' : 'border-white/[0.06] bg-ink-850')} data-id="element-1458" data-mp="3h2bqp5lj9d4u">
            <div className="text-[26px] font-semibold tracking-tight text-white" data-id="element-1459" data-mp="3m8qnvf5i0wxp">{s.value}</div>
            <div className="mt-0.5 text-[12px] text-zinc-500" data-id="element-1460" data-mp="3hfp5uy9zwh9w">{s.label}</div>
          </div>)}
      </div>

      {/* AI improvement suggestions */}
      <div className="mb-6 rounded-2xl border border-accent/20 bg-accent/[0.06] p-4" data-id="element-1461" data-mp="38h3eqeqgjtxi">
        <div className="mb-3 flex items-center gap-2 text-[13px] font-semibold text-accent-soft" data-id="element-1462" data-mp="33hqs9v3i0ovg">
          <SparklesIcon className="h-4 w-4" data-id="element-1463" data-mp="2yi4ftnvhvcnp" /> Suggested improvements
        </div>
        <ul className="space-y-2" data-id="element-1464" data-mp="2yk7e0v6swc3i">
          {automationInsights.map(ins => <li key={ins} className="flex items-start gap-2.5 text-[13px] text-zinc-300" data-id="element-1465" data-mp="2uily10yyzhp3">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-soft" data-id="element-1466" data-mp="wq85ghl2c8ui" />
              {ins}
            </li>)}
        </ul>
      </div>

      {/* Automations list */}
      <div className="space-y-3" data-id="element-1467" data-mp="1bypfv42rvvco">
        {automationList.map((a, i) => <AutomationRow key={a.id} auto={a} index={i} data-id="element-1468" data-mp="1fyptwnlym4j7" data-mpi={i} />)}
      </div>
      <CreateAutomationModal open={createOpen} onCreate={createAutomation} onClose={() => setCreateOpen(false)} data-id="element-1469" data-mp="128w6m9q3rsbl" />
    </div>;
}